import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Redirect, useParams } from "react-router-dom";
import { projects } from "../data/portfolio";
import P5Backdrop from "../components/P5Backdrop";
import {
  Arrow,
  NORTH_EAST_ARROW,
  PageMeta,
} from "../components/SiteChrome";

function GalleryItem({ item, onOpen, title }) {
  if (item.type === "video") {
    return (
      <figure className="gallery-item gallery-video">
        <video controls playsInline preload="metadata" poster={item.poster}>
          <source src={item.src} />
          Your browser does not support embedded video.
        </video>
        <figcaption>{item.alt}</figcaption>
      </figure>
    );
  }

  return (
    <figure className="gallery-item">
      <button
        className="gallery-image-button"
        type="button"
        aria-label={`Enlarge image: ${item.alt}`}
        onClick={() => onOpen(item)}
      >
        <img src={item.src} alt={item.alt} loading="lazy" />
        <span className="image-view-hint" aria-hidden="true">
          Enlarge image {NORTH_EAST_ARROW}
        </span>
      </button>
      <figcaption>{title}</figcaption>
    </figure>
  );
}

function ImageLightbox({
  currentIndex,
  images,
  onClose,
  onNext,
  onPrevious,
}) {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const image = currentIndex === null ? null : images[currentIndex];
  const isOpen = Boolean(image);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousFocus = document.activeElement;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        onPrevious();
        return;
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        onNext();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = dialogRef.current?.querySelectorAll("a, button");
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.body.classList.add("lightbox-open");
    document.addEventListener("keydown", handleKeyDown);
    closeButtonRef.current?.focus();

    return () => {
      document.body.classList.remove("lightbox-open");
      document.removeEventListener("keydown", handleKeyDown);
      previousFocus?.focus();
    };
  }, [isOpen, onClose, onNext, onPrevious]);

  if (!image) return null;

  const fullSizeSrc = image.fullSrc || image.src;
  const hasMultipleImages = images.length > 1;

  return (
    <div
      className="image-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Image ${currentIndex + 1} of ${images.length}: ${image.alt}`}
      ref={dialogRef}
    >
      <div className="lightbox-toolbar">
        <span aria-live="polite">{image.alt}</span>
        <a href={fullSizeSrc} target="_blank" rel="noreferrer">
          Open original <span aria-hidden="true">{NORTH_EAST_ARROW}</span>
          <span className="sr-only"> in a new tab</span>
        </a>
        <button type="button" onClick={onClose} ref={closeButtonRef}>
          Close <span aria-hidden="true">×</span>
        </button>
      </div>
      <div
        className="lightbox-stage"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <img src={fullSizeSrc} alt={image.alt} />
      </div>
      {hasMultipleImages && (
        <div className="lightbox-navigation">
          <button type="button" onClick={onPrevious}>
            <span aria-hidden="true">←</span> Previous
          </button>
          <span className="lightbox-count" aria-live="polite">
            {String(currentIndex + 1).padStart(2, "0")} /{" "}
            {String(images.length).padStart(2, "0")}
          </span>
          <button type="button" onClick={onNext}>
            Next <span aria-hidden="true">→</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default function ProjectPage() {
  const { slug } = useParams();
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const projectIndex = projects.findIndex((item) => item.slug === slug);
  const project = projectIndex < 0 ? null : projects[projectIndex];
  const galleryImages = project
    ? project.gallery.filter((item) => item.type === "image")
    : [];
  const lightboxImages =
    project && !galleryImages.some((item) => item.src === project.cover)
      ? [
          { src: project.cover, alt: project.coverAlt, type: "image" },
          ...galleryImages,
        ]
      : galleryImages;
  const showPreviousImage = useCallback(() => {
    setLightboxIndex((currentIndex) =>
      currentIndex === null || lightboxImages.length === 0
        ? null
        : (currentIndex - 1 + lightboxImages.length) % lightboxImages.length
    );
  }, [lightboxImages.length]);
  const showNextImage = useCallback(() => {
    setLightboxIndex((currentIndex) =>
      currentIndex === null || lightboxImages.length === 0
        ? null
        : (currentIndex + 1) % lightboxImages.length
    );
  }, [lightboxImages.length]);
  const openLightbox = (image) => {
    const imageIndex = lightboxImages.findIndex(
      (candidate) => candidate.src === image.src
    );
    if (imageIndex >= 0) setLightboxIndex(imageIndex);
  };

  if (!project) return <Redirect to="/work" />;
  const nextProject = projects[(projectIndex + 1) % projects.length];

  return (
    <>
      <PageMeta title={project.title} description={project.intro} />
      <article
        className={`project-page project-theme-${project.colour} project-${project.slug}`}
      >
        <header className="project-hero">
          <div className="project-hero-copy">
            <Link className="back-link" to="/work">
              <span aria-hidden="true">←</span> All work
            </Link>
            <span className="eyebrow">
              {project.number} · {project.meta}
            </span>
            <h1>{project.title}</h1>
            <p>{project.intro}</p>
          </div>
          <div className="project-hero-media">
            <P5Backdrop variant="field" palette="cream" />
            <button
              className="project-cover-button"
              type="button"
              aria-label={`Enlarge image: ${project.coverAlt}`}
              onClick={() =>
                openLightbox({ src: project.cover, alt: project.coverAlt })
              }
            >
              <img src={project.cover} alt={project.coverAlt} />
              <span className="image-view-hint" aria-hidden="true">
                Enlarge image {NORTH_EAST_ARROW}
              </span>
            </button>
            <span>{project.period}</span>
          </div>
        </header>

        <section className="project-statement section-pad">
          <span className="eyebrow">The work</span>
          <p>{project.body}</p>
          <div className="project-tags">
            {project.categories.map((category) => (
              <span key={category}>{category}</span>
            ))}
          </div>
          {project.externalUrl && (
            <a
              className="button button-outline"
              href={project.externalUrl}
              target="_blank"
              rel="noreferrer"
            >
              Visit live site <Arrow />
            </a>
          )}
        </section>

        {project.tracks && (
          <section className="listening-room section-pad">
            <div>
              <span className="eyebrow">Listening room</span>
              <h2>Selected recordings.</h2>
            </div>
            <div className="track-list">
              {project.tracks.map((track, index) => (
                <div className="track" key={track.src}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <strong>{track.title}</strong>
                  <audio controls preload="none">
                    <source src={track.src} />
                    Your browser does not support embedded audio.
                  </audio>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="project-gallery section-pad">
          {project.gallery.map((item, index) => (
            <GalleryItem
              item={item}
              onOpen={openLightbox}
              title={`${project.title} · ${String(index + 1).padStart(2, "0")}`}
              key={`${item.src}-${index}`}
            />
          ))}
        </section>

        <Link className="next-project" to={`/work/${nextProject.slug}`}>
          <span>
            Next project · {nextProject.number}
            <small>{nextProject.meta}</small>
          </span>
          <strong>{nextProject.title}</strong>
          <Arrow />
        </Link>
      </article>
      <ImageLightbox
        currentIndex={lightboxIndex}
        images={lightboxImages}
        onClose={closeLightbox}
        onNext={showNextImage}
        onPrevious={showPreviousImage}
      />
    </>
  );
}
